/* CreditDetailList.js — «ریز سپرده» — فرم ۱ (تملک) و فرم ۲ (هزینه ای).
   نوع فرم را سرور تعیین می‌کند (ActiveDevice=1 / DepositKindCode=2 / tblConstant.CreditDetailCode=1 ⇒ فرم ۱).
   کوئری‌ها، کنترل‌ها و پیام‌ها در deposit/views/credit_detail.py است؛ این صفحه حالت‌های فرم را دارد:
   نمایش / جدید / ویرایش (و «ویرایش جزئی»)، حذف گروهی، انتقال به بستانکاری دیگر، اسناد خزانه، سابقه استرداد. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { Field, Txt, Table, Modal, Lookup, withConfirms, visibleCols, st, toFa, numFmt, num, toEn } from './RefundDetailPage';
import { jsonOf, netErr } from '../../utils/http';

const API = (path, opts = {}) => fetch(`/api/deposit${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` }, ...opts,
}).then(jsonOf, netErr);
const NO_ROWS = [];
const BLOCK = ['ندارد', 'دارد'];                   // عدم امکان استرداد

const EMPTY1 = { id: 0, contract: '', contract_id: 0, contractor: '', contractor_code: 0, subject: '', subject_id: 0,
  payment: '', payment_kind: 0, amount: '', detail_row: '', slip_date: '', slip_time: '', slip_no: '', receipt_no: '',
  tadil_no: '', up_row: '', day_credit_no: '', remark: '', reason: '', blocked: 0, end_date: '', mofasa: '', debit: 0 };
const EMPTY2 = { id: 0, receipt_no: '', refrence_code: '', receipt_date: '', amount: '', detail_row: '', slip_date: '',
  slip_time: '', case_no: '', slip_no: '', up_row: '', remark: '', day_credit_no: '', detail_kind: 0, blocked: 0,
  reason: '', refund: 0 };

const P = {
  page: { height: '100vh', overflow: 'hidden', background: '#f0f2f7', fontFamily: "'Vazirmatn',Tahoma,sans-serif",
    direction: 'rtl', display: 'flex', flexDirection: 'column' },
  header: { background: 'linear-gradient(135deg,#0a1628,#1a2f50)', padding: '10px 24px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  back: { padding: '6px 14px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px' },
  sum: { display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '12px', padding: '6px 4px' },
};

export default function CreditDetailList({ user, onBack, credit }) {
  const base = `/credits/${credit.id}/rows`;
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const [idx, setIdx] = useState(-1);
  const [mode, setMode] = useState('view');           // view | new | edit
  const [f, setF] = useState(EMPTY1);
  const [partial, setPartial] = useState(false);      // «ویرایش جزئی»
  const [contractEditable, setContractEditable] = useState(true);
  const [info, setInfo] = useState({});
  const [showRefunds, setShowRefunds] = useState(false);   // نمایش سابقه استرداد
  const [q, setQ] = useState('');
  const [searchOn, setSearchOn] = useState(false);          // جستجو روشن/خاموش
  const [bulk, setBulk] = useState(false);                  // «حذف گروهی اقلام» ↔ «حذف اقلام انتخابی»
  const [picked, setPicked] = useState([]);
  const [move, setMove] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const firstRef = useRef(null);

  const rows = res?.data || NO_ROWS;
  const kind = res?.kind || 1;
  const head = res?.head || {};
  const caps = res?.caps || {};
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const edit = mode !== 'view';

  const load = useCallback(async (keepId) => {
    const j = await API(`${base}/`);
    if (!j.ok) { setErr(j.error || j.msg); return; }
    setRes(j); setErr('');
    const data = j.data || [];
    let k = data.length ? 0 : -1;
    if (keepId) { const p = data.findIndex(r => r.CreditDetailId === keepId); if (p >= 0) k = p; }
    setIdx(k);
  }, [base]);
  useEffect(() => { load(); }, [load]);

  /* ── نمایش ردیف انتخاب‌شده ── */
  useEffect(() => {
    if (mode !== 'view' || !res) return;
    const g = rows[idx];
    if (!g) { setF(kind === 1 ? EMPTY1 : EMPTY2); setInfo({}); return; }
    if (kind === 1) {
      setF({ ...EMPTY1, id: g.CreditDetailId,
        contract: g.ContractId ? `${g['شماره قرارداد'] ?? ''} - ${g['تاریخ قرارداد'] ?? ''} - ${g['موضوع قرارداد'] ?? ''}` : '',
        contract_id: g.ContractId, contractor: g['پیمانکار'], contractor_code: g.ContractorCode,
        subject: g['موضوع سپرده'], subject_id: g.DepositSubjectId, payment: g['نوع پرداخت'], payment_kind: g.PaymentKindId,
        amount: g['مبلغ سپرده'], detail_row: g['ردیف سپرده'], slip_date: g['تاریخ فیش'], slip_time: g['ساعت واریز'],
        slip_no: g['شماره فیش'], receipt_no: g['شماره قبض'], tadil_no: g.TadilNo, up_row: g['ردیف خزانه'],
        day_credit_no: g['بستانکاری روزانه'], remark: g['ملاحظات'], reason: g['علت منع پرداخت'],
        blocked: Number(g.IsRefundNotAllowed) || 0, end_date: g['اتمام قرارداد'], mofasa: g['مفاصاحساب بیمه'],
        debit: g['بدهی صورت وضعیت قطعی'] });
    } else {
      setF({ ...EMPTY2, id: g.CreditDetailId, receipt_no: g['قبض'], refrence_code: g['کد پیگیری'],
        receipt_date: g['تاریخ قبض'], amount: g['مبلغ سپرده'], detail_row: g['ردیف'], slip_date: g['تاریخ فیش'],
        slip_time: g['ساعت واریز'], case_no: g['پرونده'], slip_no: g['فیش'], up_row: g['ردیف خزانه'],
        remark: g['ملاحظات'], day_credit_no: g['بستانکاری روزانه'], detail_kind: Number(g.DetailKindCode) || 0,
        blocked: Number(g.IsRefundNotAllowed) || 0, reason: g['دلیل منع استرداد'], refund: g['استرداد'] });
    }
    API(`${base}/${g.CreditDetailId}/info/${showRefunds ? '?refunds=1' : ''}`).then(j => { if (j.ok) setInfo(j); });
  }, [idx, rows, mode, kind, res, base, showRefunds]);

  /* ── جستجوی درون‌گرید (از ۴ حرف به بعد خودکار) ── */
  const qs = toEn(q).trim().replace(/,/g, '');
  const numKeys = kind === 1 ? ['مبلغ سپرده', 'استرداد', 'شماره سند', 'بستانکاری روزانه', 'شماره سند کسر سپرده']
    : ['مبلغ سپرده', 'استرداد', 'بستانکاری روزانه', 'شماره سند کسر سپرده'];
  const txtKeys = kind === 1 ? ['ملاحظات', 'شماره فیش', 'شماره قبض', 'موضوع سپرده', 'پیمانکار', 'شماره قرارداد', 'موضوع قرارداد']
    : ['ملاحظات', 'فیش', 'قبض'];
  const shown = rows.map((r, k) => ({ r, k })).filter(({ r }) => !(searchOn && qs) ||
    (/^\d+$/.test(qs) && numKeys.some(c => String(r[c] ?? '') === qs)) ||
    txtKeys.some(c => String(r[c] ?? '').includes(qs)));
  const cols = visibleCols(res?.columns, rows);
  const sumDetail = rows.reduce((a, r) => a + Number(r['مبلغ سپرده'] || 0), 0);
  const sumRefund = rows.reduce((a, r) => a + Number(r['استرداد'] || 0), 0);

  /* ── جدید / ویرایش ── */
  const doNew = async () => {
    const j = await API(`${base}/next-row/`);
    setF({ ...(kind === 1 ? EMPTY1 : EMPTY2), detail_row: j.ok ? j.next_row : '' });
    setPartial(false); setContractEditable(true); setInfo({}); setMode('new');
    setTimeout(() => firstRef.current?.focus(), 50);
  };
  const doEdit = async () => {
    if (!f.id) return;
    const j = await API(`${base}/${f.id}/edit-check/`, { method: 'POST' });
    if (!j.ok) { alert(j.error || j.msg); return; }
    if (j.msg) alert(j.msg);
    setPartial(!!j.partial); setContractEditable(!!j.contract_editable); setMode('edit');
  };
  const doCancel = () => { setMode('view'); setPartial(false); };

  /* ── ثبت ── */
  const save = async () => {
    setBusy(true);
    const payload = { ...f, detail_row: num(f.detail_row), amount: num(f.amount), subject_id: f.subject_id,
      tadil_no: num(f.tadil_no) };
    const url = mode === 'edit' ? `${base}/${f.id}/save/` : `${base}/save/`;
    const j = await API(url, { method: 'POST', body: JSON.stringify(payload) });
    setBusy(false);
    if (!j.ok) { if (j.error || j.msg) alert(j.error || j.msg); return; }
    if (mode === 'edit') { setMode('view'); setPartial(false); await load(j.id); }
    else { await load(j.id); setF({ ...(kind === 1 ? EMPTY1 : EMPTY2), detail_row: j.next_row }); setInfo({});
      setTimeout(() => firstRef.current?.focus(), 50); }
  };

  /* ── حذف / حذف گروهی ── */
  const del = async () => {
    if (!f.id) return;
    const j = await withConfirms(`${base}/${f.id}/delete/`, {});
    if (!j) return;
    if (!j.ok) { alert(j.error || j.msg); return; }
    load();
  };
  const deleteAll = async () => {
    if (!rows.length) return;
    if (!bulk) { setBulk(true); setPicked([]); alert('اقلام مورد نظر برای حذف را انتخاب کنید'); return; }
    if (!picked.length) { alert('هیچ آرتیکلی انتخاب نشده است'); setBulk(false); return; }
    const j = await API(`${base}/delete-all/`, { method: 'POST', body: JSON.stringify({ ids: picked }) });
    if (!j.ok) { alert(j.error || j.msg); return; }
    (j.messages || []).forEach(m => alert(m));
    setBulk(false); setPicked([]); load();
  };

  /* ── سابقه استرداد ── */
  const openHistory = async (byContract) => {
    if (kind === 1 && !num(f.detail_row)) { alert('اطلاعات سپرده برای نمایش سابقه استرداد کافی نیست'); return; }
    if (kind === 2) {
      if (!num(f.detail_row)) { alert('اطلاعات سپرده برای نمایش سابقه استرداد کافی نیست'); return; }
      if (!Number(f.refund)) { alert('این سپرده استردادی نداشته است'); return; }
    }
    const p = byContract ? { mode: 'contract', contract_id: f.contract_id }
      : { credit_no: head.no, credit_year: head.year, credit_row: num(f.detail_row) };
    const j = await API(`/refund-details/history/?${new URLSearchParams(p).toString()}`);
    if (!j.ok) { alert(j.error || j.msg); return; }
    setHistory({ ...j, byContract, contract_id: f.contract_id });
  };

  /* ── انتقال ریز به بستانکاری دیگر ── */
  const openMove = () => {
    if (!f.id) return;
    const g = rows[idx] || {};
    setMove({ mode: 'one', new_no: g['شماره بستانکاری'] || '', new_year: Number(g['سال بستانکاری']) || head.year,
      from_date: f.slip_date, to_date: f.slip_date, from_row: '', to_row: '' });
  };

  /* ── سپرده های سررسید شده اسناد خزانه ── */
  const openTreasury = async () => {
    const j = await API(`${base}/treasury/`);
    if (!j.ok) { alert(j.error || j.msg); return; }
    setTreasury({ rows: j.data, columns: j.columns, sel: Object.fromEntries(j.data.map(r =>
      [r.TreasuryPreceptDetailDeductionId, Number(r.Selection) === 1])), q: '' });
  };

  const T = (k, ro, extra = {}) => (
    <Txt value={f[k]} onChange={v => set(k, v)} ro={!edit || ro} dir={extra.dir} inputRef={extra.inputRef}
      onKeyDown={e => { if (e.key === 'Escape' && edit) doCancel(); }} />
  );
  const D = (k, ro) => (!edit || ro) ? <Txt value={f[k]} ro dir="ltr" />
    : <JalaliDatePicker value={f[k]} onChange={v => set(k, v)} />;
  const lockAll = partial;                          // «ویرایش جزئی»
  const rowStyle = r => (bulk && picked.includes(r.CreditDetailId)) ? { background: '#ffcdd2' } : {};
  const lk = what => t => API(`${base}/lookup/${what}/?q=${encodeURIComponent(t)}`);

  return (
    <div style={P.page}>
      <header style={P.header}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={onBack} style={P.back}>← بازگشت</button>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>{res?.title || 'ریز سپرده'}</div>
        </div>
        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>{user?.full_name}</span>
      </header>
      <TabBar />

      <div style={st.bar}>
        {!edit && <>
          {caps.new && <button onClick={doNew} style={st.btn('#2e7d32')}>＋ جدید</button>}
          {caps.edit && <button onClick={doEdit} disabled={!f.id} style={{ ...st.btn('#1565c0'), opacity: f.id ? 1 : .4 }}>✎ ویرایش</button>}
          {caps.delete && <button onClick={del} disabled={!f.id} style={{ ...st.btn('#c62828'), opacity: f.id ? 1 : .4 }}>✕ حذف</button>}
          {caps.delete_all && <button onClick={deleteAll} title="برای حذف گروهی باید سطرهای مورد نظر را انتخاب کنید"
            onContextMenu={e => { e.preventDefault(); setBulk(false); setPicked([]); }}
            style={st.btn(bulk ? '#b71c1c' : '#8d6e63')}>{bulk ? 'حذف اقلام انتخابی' : 'حذف گروهی اقلام'}</button>}
          {caps.move && <button onClick={openMove} disabled={!f.id} style={{ ...st.btn('#6a1b9a'), opacity: f.id ? 1 : .4 }}>
            انتقال ریز به بستانکاری دیگر</button>}
          {caps.history && (kind === 1 || Number(f.refund) > 0) &&
            <button onClick={() => openHistory(false)} style={st.btn('#455a64')}>سابقه استرداد</button>}
          {caps.treasury && <button onClick={openTreasury} style={st.btn('#00695c')}>سپرده های سررسید شده اسناد خزانه</button>}
          <label style={{ fontSize: '12px', display: 'flex', gap: '4px', alignItems: 'center' }}>
            <input type="checkbox" checked={showRefunds} onChange={e => setShowRefunds(e.target.checked)} />سابقه استرداد</label>
          <label style={{ fontSize: '12px', display: 'flex', gap: '4px', alignItems: 'center', marginRight: 'auto' }}>
            <input type="checkbox" checked={searchOn} onChange={e => setSearchOn(e.target.checked)} />جستجو</label>
          <input value={q} placeholder="جستجو" style={{ ...st.input(false), width: '200px' }}
            onChange={e => { setQ(e.target.value); if (e.target.value.trim().length >= 4) setSearchOn(true);
              if (!e.target.value.trim()) setSearchOn(false); }} />
        </>}
        {edit && <>
          {caps.ok && <button onClick={save} disabled={busy} style={st.btn('#2e7d32')}>{busy ? '⏳' : '✔ ثبت'}</button>}
          <button onClick={doCancel} style={st.btn('#757575')}>لغو</button>
          <span style={{ fontSize: '12px', color: '#666' }}>{mode === 'new' ? 'ثبت ریز جدید' : (partial ? 'ویرایش جزئی' : 'ویرایش ریز')}</span>
        </>}
      </div>

      <div style={st.body}>
        {err && <div style={st.err}>{err}</div>}
        <div style={st.card}>
          {info.black_list && !edit && <div style={{ ...st.err, marginBottom: '8px', fontWeight: 700 }}>{info.black_list}</div>}
          {kind === 1 ? (
            <div style={st.grid}>
              <Field label="قرارداد" span={2}>
                <Lookup value={f.contract} ro={!edit || (lockAll && !contractEditable)} onText={v => set('contract', v)}
                  fetcher={lk('contract')} show={['کد', 'تاریخ', 'عنوان', 'پیمانکار']}
                  onPick={async r => {
                    setF(p => ({ ...p, contract_id: r.Id, contract: `${r['کد']} ${r['تاریخ']} ${r['عنوان']}` }));
                    const j = await API(`${base}/lookup/contract-info/?id=${r.Id}`);
                    if (j.ok) setF(p => ({ ...p, contractor_code: j.contractor_code, contractor: j.contractor }));
                  }} />
              </Field>
              <Field label="پیمانکار" span={2}>
                <Lookup value={f.contractor} ro={!edit || !!f.contract_id || (lockAll && !contractEditable)}
                  onText={v => setF(p => ({ ...p, contractor: v, contractor_code: 0 }))}
                  fetcher={lk('contractor')} onPick={r => setF(p => ({ ...p, contractor_code: r.Id, contractor: r['عنوان'] }))} />
              </Field>
              {f.end_date ? <Field label="اتمام قرارداد"><Txt value={f.end_date} ro dir="ltr" /></Field> : null}
              {f.mofasa ? <Field label="مفاصاحساب بیمه"><Txt value={f.mofasa} ro dir="ltr" /></Field> : null}
              {Number(f.debit) ? <Field label="بدهی صورت وضعیت قطعی"><Txt value={numFmt(f.debit)} ro dir="ltr" color="#c62828" /></Field> : null}
              <Field label="موضوع سپرده" span={2}>
                <Lookup value={f.subject} ro={!edit || lockAll} onText={v => setF(p => ({ ...p, subject: v, subject_id: 0 }))}
                  fetcher={lk('subject')} onPick={r => setF(p => ({ ...p, subject_id: r.Id, subject: r['عنوان'] }))} />
              </Field>
              <Field label="نوع پرداخت">
                <Lookup value={f.payment} ro={!edit || lockAll} onText={v => setF(p => ({ ...p, payment: v, payment_kind: 0 }))}
                  fetcher={lk('payment-kind')} onPick={r => setF(p => ({ ...p, payment_kind: r.Id, payment: r['عنوان'] }))} />
              </Field>
              <Field label="ردیف سپرده">{T('detail_row', lockAll, { dir: 'ltr', inputRef: firstRef })}</Field>
              <Field label="مبلغ سپرده">{edit ? T('amount', lockAll, { dir: 'ltr' }) : <Txt value={numFmt(f.amount)} ro dir="ltr" />}</Field>
              <Field label="تاریخ فیش">{D('slip_date', lockAll)}</Field>
              <Field label="ساعت واریز"><Txt value={f.slip_time} ro dir="ltr" /></Field>
              <Field label="شماره فیش">{T('slip_no', lockAll, { dir: 'ltr' })}</Field>
              <Field label="شماره قبض">{T('receipt_no', lockAll, { dir: 'ltr' })}</Field>
              <Field label="تعدیل">{T('tadil_no', lockAll, { dir: 'ltr' })}</Field>
              <Field label="ردیف خزانه"><Txt value={f.up_row} ro dir="ltr" /></Field>
              <Field label="بستانکاری روزانه">{T('day_credit_no', false, { dir: 'ltr' })}</Field>
              <Field label="ملاحظات" span={3}>{T('remark', false)}</Field>
              <Field label="منع استرداد">
                <select value={f.blocked} disabled={!edit} onChange={e => set('blocked', Number(e.target.value))} style={st.input(!edit)}>
                  {BLOCK.map((t, n) => <option key={n} value={n}>{t}</option>)}</select>
              </Field>
              <Field label="علت منع پرداخت" span={2}>{T('reason', false)}</Field>
              <Field label="سند / تاریخ سند"><Txt value={info.document_no ? `${toFa(info.document_no)}  ${toFa(info.document_date)}` : ''} ro /></Field>
            </div>
          ) : (
            <div style={st.grid}>
              <Field label="قبض" span={2}>{T('receipt_no', lockAll, { dir: 'ltr' })}</Field>
              <Field label="کد پیگیری">{T('refrence_code', lockAll, { dir: 'ltr' })}</Field>
              <Field label="تاریخ قبض">{D('receipt_date', lockAll)}</Field>
              <Field label="ردیف">{T('detail_row', lockAll, { dir: 'ltr' })}</Field>
              <Field label="مبلغ سپرده">{edit ? T('amount', lockAll, { dir: 'ltr' }) : <Txt value={numFmt(f.amount)} ro dir="ltr" />}</Field>
              <Field label="فیش">{T('slip_no', lockAll, { dir: 'ltr', inputRef: firstRef })}</Field>
              <Field label="تاریخ فیش">{D('slip_date', lockAll)}</Field>
              <Field label="ساعت واریز"><Txt value={f.slip_time} ro dir="ltr" /></Field>
              <Field label="پرونده">{T('case_no', lockAll)}</Field>
              <Field label="ردیف خزانه"><Txt value={f.up_row} ro dir="ltr" /></Field>
              <Field label="بستانکاری روزانه">{T('day_credit_no', lockAll, { dir: 'ltr' })}</Field>
              <Field label="نوع ریز">
                <select value={f.detail_kind} disabled={!edit || lockAll} onChange={e => set('detail_kind', Number(e.target.value))}
                  style={st.input(!edit || lockAll)}>
                  {(res?.detail_kinds || []).map((t, n) => <option key={n} value={n}>{t}</option>)}</select>
              </Field>
              <Field label="ملاحظات" span={3}>{T('remark', lockAll)}</Field>
              <Field label="منع استرداد">
                <select value={f.blocked} disabled={!edit} onChange={e => set('blocked', Number(e.target.value))} style={st.input(!edit)}>
                  {BLOCK.map((t, n) => <option key={n} value={n}>{t}</option>)}</select>
              </Field>
              <Field label="دلیل منع استرداد" span={2}>{T('reason', false)}</Field>
              <Field label="سند / تاریخ سند"><Txt value={info.document_no ? `${toFa(info.document_no)}  ${toFa(info.document_date)}` : ''} ro /></Field>
            </div>
          )}
          <div style={P.sum}>
            <span>تعداد ریز: <b>{toFa(rows.length)}</b></span>
            <span>وارد شده: <b>{numFmt(sumDetail)}</b></span>
            <span>وارد نشده: <b>{numFmt((head.amount || 0) - sumDetail)}</b></span>
            <span>استرداد شده: <b>{numFmt(sumRefund)}</b></span>
            <span>مانده: <b>{numFmt((head.amount || 0) - sumRefund)}</b></span>
          </div>
        </div>

        {showRefunds && !edit && (info.refunds || []).length > 0 && (
          <div style={st.card}>
            <Table cols={visibleCols(info.refund_columns, info.refunds)} rows={info.refunds} maxH="22vh" />
          </div>)}

        <div style={st.card}>
          <Table cols={cols} rows={shown.map(x => x.r)} rowStyle={rowStyle}
            selKey={bulk ? undefined : shown.findIndex(x => x.k === idx)}
            onPick={(r, k) => {
              if (edit) return;
              if (bulk) setPicked(p => p.includes(r.CreditDetailId) ? p.filter(x => x !== r.CreditDetailId) : [...p, r.CreditDetailId]);
              else setIdx(shown[k].k);
            }}
            onDouble={() => { if (!edit && !bulk && caps.edit) doEdit(); }} maxH="42vh" />
        </div>
      </div>

      {history && (
        <Modal title="سابقه استرداد" onClose={() => setHistory(null)} width="1100px"
          footer={<span style={{ fontSize: '12px', display: 'flex', gap: '18px', alignItems: 'center' }}>
            {kind === 1 && history.contract_id > 0 && <>
              <label><input type="radio" checked={!history.byContract} onChange={() => openHistory(false)} /> ردیف سپرده</label>
              <label><input type="radio" checked={!!history.byContract} onChange={() => openHistory(true)} /> قرارداد</label></>}
            <span>جمع سپرده: <b>{numFmt(history.sum_detail)}</b></span>
            <span>جمع استرداد: <b>{numFmt(history.sum_refund)}</b></span>
            <span>مانده: <b>{numFmt(history.remain)}</b></span></span>}>
          <Table cols={visibleCols(history.columns, history.data)} rows={history.data} maxH="60vh" />
        </Modal>)}

      {move && <MoveDialog base={base} rdid={f.id} head={head} move={move} setMove={setMove}
        onDone={() => { setMove(null); load(); }} />}

      {treasury && <TreasuryDialog base={base} t={treasury} setT={setTreasury} onDone={() => { setTreasury(null); load(); }} />}
    </div>
  );
}

/* انتقال ریز به بستانکاری دیگر */
function MoveDialog({ base, rdid, head, move, setMove, onDone }) {
  const m = (k, v) => setMove(p => ({ ...p, [k]: v }));
  const setGroup = async g => {
    if (!g) { m('mode', 'one'); return; }
    const j = await API(`${base}/move-info/`);
    setMove(p => ({ ...p, mode: 'group', from_date: j.min_date || '', to_date: j.max_date || '' }));
  };
  const newCredit = async () => {
    const j = await API(`${base}/move-info/?year=${num(move.new_year) || head.year}`);
    if (j.ok) m('new_no', j.new_credit_no);
  };
  const go = async () => {
    const j = await withConfirms(`${base}/${rdid}/move/`, { ...move, new_no: num(move.new_no), new_year: num(move.new_year),
      from_row: num(move.from_row), to_row: num(move.to_row) });
    if (!j) return;
    if (!j.ok) { alert(j.error || j.msg); return; }
    onDone();
  };
  const g = move.mode === 'group';
  return (
    <Modal title="انتقال ریز به بستانکاری دیگر" onClose={() => setMove(null)} width="640px"
      footer={<><button onClick={go} style={st.btn('#2e7d32')}>انتقال</button>
        <button onClick={() => setMove(null)} style={st.btn('#757575')}>بستن</button></>}>
      <div style={st.grid}>
        <Field label="شماره بستانکاری مقصد"><Txt value={move.new_no} onChange={v => m('new_no', v)} dir="ltr" /></Field>
        <Field label="سال بستانکاری مقصد"><Txt value={move.new_year} onChange={v => m('new_year', v)} dir="ltr" /></Field>
        <Field label=" "><button onClick={newCredit} style={st.btn('#ef6c00')}>ایجاد بستانکاری جدید</button></Field>
      </div>
      <div style={{ display: 'flex', gap: '18px', margin: '12px 0', fontSize: '12.5px' }}>
        <label><input type="radio" checked={!g} onChange={() => setGroup(false)} /> ردیف سپرده جاری</label>
        <label><input type="radio" checked={g} onChange={() => setGroup(true)} /> گروهی</label>
      </div>
      <div style={st.grid}>
        <Field label="واریز شده از تاریخ">{g ? <JalaliDatePicker value={move.from_date} onChange={v => m('from_date', v)} />
          : <Txt value={move.from_date} ro dir="ltr" />}</Field>
        <Field label="تا">{g ? <JalaliDatePicker value={move.to_date} onChange={v => m('to_date', v)} />
          : <Txt value={move.to_date} ro dir="ltr" />}</Field>
        <Field label="ردیف سپرده از"><Txt value={move.from_row} ro={!g} onChange={v => m('from_row', v)} dir="ltr" /></Field>
        <Field label="تا"><Txt value={move.to_row} ro={!g} onChange={v => m('to_row', v)} dir="ltr" /></Field>
      </div>
      {g && <div style={{ fontSize: '11px', color: '#777', marginTop: '6px' }}>
        اگر هر دو تاریخ کامل باشد بازهٔ تاریخ فیش، وگرنه بازهٔ ردیف ملاک انتقال است.</div>}
    </Modal>
  );
}

/* سپرده های سررسید شده اسناد خزانه */
function TreasuryDialog({ base, t, setT, onDone }) {
  const [busy, setBusy] = useState(false);
  const qs = toEn(t.q).trim();
  const rows = t.rows.filter(r => !qs || String(r['سند خزانه'] ?? '').includes(qs) || String(r['قرارداد'] ?? '').includes(qs)
    || (/^\d+$/.test(qs) && String(r['مبلغ سپرده']) === qs));
  const picked = t.rows.filter(r => t.sel[r.TreasuryPreceptDetailDeductionId]);
  const sum = picked.reduce((a, r) => a + Number(r['مبلغ سپرده'] || 0), 0);
  const setAll = fn => setT(p => ({ ...p, sel: Object.fromEntries(p.rows.map(r =>
    [r.TreasuryPreceptDetailDeductionId, fn(p.sel[r.TreasuryPreceptDetailDeductionId])])) }));
  const key = e => {                     // «-» هیچ، «+» همه، «*» معکوس
    if (e.key === '-') { e.preventDefault(); setAll(() => false); }
    if (e.key === '+') { e.preventDefault(); setAll(() => true); }
    if (e.key === '*') { e.preventDefault(); setAll(v => !v); }
  };
  const cols = visibleCols(t.columns, t.rows);
  const go = async () => {
    setBusy(true);
    const j = await API(`${base}/treasury/`, { method: 'POST',
      body: JSON.stringify({ ids: picked.map(r => r.TreasuryPreceptDetailDeductionId) }) });
    setBusy(false);
    if (!j.ok) { alert(j.error || j.msg); return; }
    onDone();
  };
  return (
    <Modal title="سپرده های سررسید شده اسناد خزانه" onClose={() => setT(null)} width="1200px"
      footer={<><span style={{ fontSize: '12px', marginLeft: 'auto' }}>جمع: <b>{numFmt(sum)}</b> &nbsp; تعداد: <b>{toFa(picked.length)}</b></span>
        <button onClick={go} disabled={busy || !picked.length} style={st.btn('#2e7d32')}>{busy ? '⏳' : 'انتقال به بستانکاری'}</button></>}>
      <div tabIndex={0} onKeyDown={key} style={{ outline: 'none' }}>
        <input placeholder="جستجو" value={t.q} onChange={e => setT(p => ({ ...p, q: e.target.value }))}
          style={{ ...st.input(false), width: '240px', marginBottom: '8px' }} />
        <span style={{ fontSize: '11px', color: '#777', marginRight: '10px' }}>کلید «+» همه، «-» هیچ، «*» معکوس</span>
        <div style={{ overflow: 'auto', maxHeight: '58vh', border: '1px solid #eee', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={st.th}>انتخاب</th>{cols.map(c => <th key={c} style={st.th}>{c}</th>)}</tr></thead>
            <tbody>{rows.map(r => {
              const id = r.TreasuryPreceptDetailDeductionId;
              return (
                <tr key={id} style={{ background: t.sel[id] ? '#e8f5e9' : '#fff' }}>
                  <td style={st.td}><input type="checkbox" checked={!!t.sel[id]}
                    onChange={() => setT(p => ({ ...p, sel: { ...p.sel, [id]: !p.sel[id] } }))} /></td>
                  {cols.map(c => <td key={c} style={{ ...st.td, textAlign: c === 'مبلغ سپرده' ? 'left' : 'center' }}>
                    {c === 'مبلغ سپرده' ? numFmt(r[c]) : toFa(r[c])}</td>)}
                </tr>);
            })}</tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
